import { createLazyFileRoute, useNavigate } from "@tanstack/react-router";

import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { ErrorPage } from "@/components/ErrorPage";
import { LoadingPage } from "@/components/LoadingPage";
import { Breadcrumb } from "@/components/ui/breadcrumbs";
import { Button } from "@/components/ui/button";
import { MaterialDetailForm } from "@/features/admin/components/materials/MaterialDetailForm";
import { Section } from "@/features/admin/components/Section";
import type { MaterialSpecDetail } from "@/features/admin/types";
import { mutationResponse } from "@/utils/trpc";

export const Route = createLazyFileRoute("/_auth/admin/materials/$id/edit")({
  component: RouteComponent,
});

function RouteComponent() {
  const params = Route.useParams();

  const {
    data: material,
    isError,
    isPending,
    error,
  } = trpc.admin.materialSpec.get.useQuery(params);

  if (isPending) {
    return <LoadingPage />;
  }

  if (!material) {
    return <ErrorPage error={new Error("Material not found")} />;
  }

  if (isError) {
    return <ErrorPage error={error} />;
  }

  return (
    <>
      <Section.Title
        breadcrumb={
          <>
            <Breadcrumb href={{ to: "/admin/materials" }}>Materials</Breadcrumb>
            <Breadcrumb>{material.name}</Breadcrumb>
          </>
        }
        actions={
          <ConfirmDeleteDialog material={material}>
            <Button variant="destructive">Delete</Button>
          </ConfirmDeleteDialog>
        }
      >
        {material.name}
      </Section.Title>
      <MaterialDetailForm material={material} />
    </>
  );
}

function ConfirmDeleteDialog({
  material,
  children,
}: {
  material: NonNullable<MaterialSpecDetail>;
  children: React.ReactNode;
}) {
  const navigate = useNavigate();
  const deleteMaterial = trpc.admin.materialSpec.delete.useMutation(
    mutationResponse({
      errorTitle: "Error deleting material",
      successMessage: "Material deleted",
      onSuccess: () => {
        navigate({ to: "/admin/materials" });
      },
    }),
  );

  return (
    <ConfirmPrompt
      title={`Delete ${material.name}`}
      message={`Are you sure you want to delete ${material.name}?`}
      onConfirm={() => {
        deleteMaterial.mutate({ id: material.id });
      }}
      loading={deleteMaterial.isPending}
      warningText="This action cannot be undone."
      danger
    >
      {children}
    </ConfirmPrompt>
  );
}
